// /rate surface events: whole rooms getting graded, and single teams too.
//
// Its own module for the same reason submitAnalytics.js is: a standalone page
// with no funnel in common with the mock/projlab/live modules. Gating is
// inherited from analyticsGate.js.
//
// 🔴 THE PROPERTY DISCIPLINE IS THE PAYLOAD DISCIPLINE (Sep 4 2026). No league
// name, no team or manager name, no email address and no player name ever
// enters an event property on this surface, in any event, for any reason. It is
// the same rule the /api/rate-room body follows and it is enforced the same
// way: rateAnalytics.test.js hands every capture function a fixture whose names
// are known strings and asserts none of them reach posthog. Every function here
// therefore builds its payload FIELD BY FIELD out of scalars — none of them
// spreads its argument — so a caller that passes a whole report cannot leak one
// through. That is not stylistic; it is the only reason the test can pass.
//
// 🔴 AND THE LIST IS CLOSED. These events exist because /rate is about to get
// traffic and nobody can currently tell where in the funnel people stop. Adding
// a property is a decision about what this product records about its users, so
// it happens deliberately, here, with the test updated in the same commit.

import posthog from "posthog-js";

function safeCapture(event, properties) {
  try {
    posthog.capture(event, properties);
  } catch {
    /* ignore */
  }
}

// Room heat arrives as a percent (+7.3). Bucketed before capture so the event
// carries a category, not a number that could re-identify a specific room.
export function roomHeatBucket(heatPct) {
  if (!Number.isFinite(heatPct)) return "unknown";
  if (heatPct < -10) return "under_10_plus";
  if (heatPct < -3) return "under_3_to_10";
  if (heatPct <= 3) return "near_market";
  if (heatPct <= 10) return "over_3_to_10";
  return "over_10_plus";
}

/**
 * Fired once per successfully graded league.
 *
 * 🔴 NO LEAGUE ID AND NO NAMES — team names render on the page but never
 * enter an event. Format, size, budget, the keeper flag and the heat bucket
 * are the facts worth knowing: which rooms the feature actually reaches and
 * how hot they run.
 */
export function captureLeagueRated({ format, teams, budget, keeperFlag, roomHeat }) {
  safeCapture("league_rated", {
    format: format || null,
    teams: Number.isFinite(teams) ? teams : null,
    budget: Number.isFinite(budget) ? budget : null,
    keeperFlag: !!keeperFlag,
    roomHeatBucket: roomHeatBucket(roomHeat),
  });
}

/**
 * Fired once per single team graded on the "Rate my team" tab.
 *
 * 🔴 SAME RULE AS league_rated, AND ONE MORE. No league id, no league name,
 * and NO PLAYER NAMES: a roster is the most identifying thing on this page,
 * and a team_rated carrying one would make the event a record of what a named
 * person drafted. What is worth knowing is which rooms the tab reaches and
 * whether people bother entering prices, since prices are the difference
 * between a projection and a grade.
 *
 * `usedSavedLeague` is the one funnel fact here: it says whether the settings
 * step was skipped by a league the user already had, which is the cheapest
 * path through this tab and the one worth widening.
 */
/**
 * The email gate on the team report: shown once per report, unlocked once ever.
 *
 * 🔴 NO EMAIL ADDRESS IN EITHER EVENT, AND NO ROSTER. The address goes to the
 * signup endpoint and nowhere else; an analytics event carrying one would turn
 * a funnel measurement into a copy of the mailing list. `format` and
 * `hasPrices` are what is worth knowing: which rooms reach the gate, and
 * whether a priced report (the one with a grade behind the gate) converts
 * better than an unpriced one.
 */
export function captureRateGateShown({ format, hasPrices }) {
  safeCapture("rate_gate_shown", {
    format: format || null,
    hasPrices: !!hasPrices,
  });
}

/**
 * Fired on a successful signup that opened the breakdown, from either ask.
 *
 * 🔴 `source` WAS ADDED, NOTHING WAS REMOVED (Sep 4 2026). This event has been
 * shipping with `format` and `hasPrices`, so dropping either to "match" a new
 * spec would silently redefine a live series with no way to unmix the halves —
 * the same mistake the `is_sflex` revert records. The new dimension is which
 * path the reader came in on, because that is the funnel question: whether a
 * pasted room converts the gate better than a hand-typed one.
 */
export function captureRateGateUnlocked({ format, hasPrices, source }) {
  safeCapture("rate_gate_unlocked", {
    format: format || null,
    hasPrices: !!hasPrices,
    source: source || null,
  });
}

// ---------------------------------------------------------------------------
// The funnel joints (Sep 4 2026)
//
// One event per place a reader can stop. Read in order they answer: did the
// paste read, did the league grade, did a typed room ever get finished, did the
// report get looked at, did anyone open a seat, did the gate convert, and are
// the two shortcuts (a saved league, a screenshot) worth their code.

/**
 * A pasted draft handed to the grader.
 *
 * 🔴 `ok` REPORTS THE PARSE, NOT THE BUTTON. It is `read.ok` rather than a
 * literal true so that a future entry point which submits a partial read cannot
 * quietly record a success. Today the fill control only exists once a paste has
 * read, so this is true in practice; the paste FAILURE rate is a different
 * measurement, taken where a failed parse is visible, and it is not this event.
 */
export function captureRatePasteSubmitted({ ok, teams, format }) {
  safeCapture("rate_paste_submitted", {
    ok: !!ok,
    teams: Number.isFinite(teams) ? teams : null,
    format: format || null,
  });
}

/**
 * A paste READ, at the moment it is read — every attempt, success or failure.
 *
 * 🔴 THIS IS THE EVENT WITH A REAL FAILURE RATE IN IT, and that is why it
 * exists next to rate_paste_submitted rather than instead of it. `submitted`
 * fires where a paste is handed to the grader, and the fill control only
 * appears once a paste has already read — so its `ok` is true in practice and
 * the pastes that never read are invisible to it. This one fires at the parse,
 * where a failure is the observable outcome.
 *
 * 🔴 ONE EVENT FOR BOTH OUTCOMES, ON PURPOSE. The failure rate is then a ratio
 * inside a single series rather than a subtraction between two of them, which
 * is the difference between a number anyone can read off a chart and a number
 * that needs a join and gets computed wrong once.
 *
 * 🔴 AND NOT ONE FRAGMENT OF THE PASTE, EVER. A pasted draft is nothing but
 * player, team and manager names, so no excerpt, no truncation, no sample line
 * and no length may travel: `reason` is a value from a closed enum
 * (PASTE_FAIL_REASONS in pasteDraft.js) and it is the only thing said about a
 * failure. The evidence lines that actually teach the parser are shown to the
 * USER, who sends them if they choose; that is a different mechanism and it
 * stays that way.
 *
 * `reason` is present only on a failure. A success has no reason and gets no
 * key rather than a null one: absent, never defaulted, the same rule the rest
 * of this surface runs on.
 */
export function captureRatePasteParsed({ ok, reason }) {
  safeCapture("rate_paste_parsed", ok ? { ok: true } : { ok: false, reason: reason || null });
}

/** A Sleeper league fetched and graded, server round trip and all. */
export function captureRateSleeperGraded({ teams, format }) {
  safeCapture("rate_sleeper_graded", {
    teams: Number.isFinite(teams) ? teams : null,
    format: format || null,
  });
}

/**
 * A hand-entered room reaching its last seat.
 *
 * 🔴 COMPLETED MEANS EVERY SEAT IS IN; `priced` IS THE SEPARATE QUESTION. A
 * room typed without dollars is finished as a roster and ungradeable as a
 * market, and those are two different places to lose somebody: one is a data
 * entry cost, the other is a comprehension problem. Folding them into one
 * boolean would hide whichever is the real one.
 */
export function captureRateManualCompleted({ teams, format, priced }) {
  safeCapture("rate_manual_completed", {
    teams: Number.isFinite(teams) ? teams : null,
    format: format || null,
    priced: !!priced,
  });
}

/**
 * The room report on screen, from any of the three ways in.
 *
 * `source` is "paste" | "sleeper" | "manual" — the three provenances the page
 * itself distinguishes. Fired from RoomReport, which is the one component all
 * three render, so the three cannot come to mean different moments.
 */
export function captureRateReportViewed({ source, teams, format }) {
  safeCapture("rate_report_viewed", {
    source: source || null,
    teams: Number.isFinite(teams) ? teams : null,
    format: format || null,
  });
}

/** A seat opened out of a room report, on any path. */
export function captureRateTeamOpened({ source }) {
  safeCapture("rate_team_opened", { source: source || null });
}

/**
 * A saved league picked up on the team tab.
 *
 * 🔴 NO PROPERTIES AT ALL, DELIBERATELY. The only facts on hand here are the
 * league's NAME and its settings, and the name is exactly what may not travel.
 * Its size and budget arrive on the report event a moment later anyway, so this
 * event is a count and nothing more.
 */
export function captureRateSavedLeagueOpened() {
  safeCapture("rate_saved_league_opened", {});
}

/**
 * A roster screenshot handed to the reader.
 *
 * `ok` is whether anything landed in the ladder — the only question worth
 * asking about a feature whose whole promise is saving typing. No image, no
 * file name, no read text, no player names: the read output IS a roster.
 */
export function captureRateScreenshotUsed({ ok }) {
  safeCapture("rate_screenshot_used", { ok: !!ok });
}

export function captureTeamRated({ format, teams, budget, hasPrices, usedSavedLeague }) {
  safeCapture("team_rated", {
    format: format || null,
    teams: Number.isFinite(teams) ? teams : null,
    budget: Number.isFinite(budget) ? budget : null,
    hasPrices: !!hasPrices,
    usedSavedLeague: !!usedSavedLeague,
  });
}
